import { useEffect } from 'react';
import urlTemplate from 'url-template';
import type { PermissionsResponse } from 'src/apis/authorization-service/types';
import type { AuthorizationServiceRequest } from 'src/apis/authorization-service/use-authorization-service';
import type { Successful } from 'src/hooks/use-axios';
import { useAxios } from 'src/hooks/use-axios';
import { Forbidden } from 'src/utils/errors';

type HookProps = {
  authorizationService?: AuthorizationServiceRequest;
};

type ExecuteProps = {
  authorizationService: Successful<AuthorizationServiceRequest>;
};

export function usePermissions({ authorizationService }: HookProps) {
  const request = useAxios(
    (axios, baseConfig, { authorizationService }: ExecuteProps) => {
      if (!authorizationService.response.data._links.permissions)
        throw new Forbidden();

      const url = urlTemplate
        .parse(authorizationService.response.data._links.permissions.href)
        .expand({});

      return axios.request<PermissionsResponse>({
        ...baseConfig,
        url,
      });
    },
    { neededOnPageLoad: true }
  );

  useEffect(() => {
    if (!authorizationService?.response) return;
    request.execute({ authorizationService });
    // oxlint-disable-next-line exhaustive-deps
  }, [authorizationService?.response]);

  if (!authorizationService) return;

  return request;
}

export type PermissionsRequest = ReturnType<typeof usePermissions>;
